// src/Pages/Messages.jsx (or wherever this file lives)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  watchThreadsForUser,
  watchMessages,
  sendMessage,
  markThreadRead,
} from "../firebase";

// moderation (shared FE/BE)
import { buildWindowText, moderateMessage } from "../lib/moderation";

/** -------------------- tiny helpers -------------------- **/
const MAX_LEN = 4000;

function snippet(s = "", n = 80) {
  const x = String(s || "");
  return x.length > n ? x.slice(0, n - 1) + "…" : x;
}

function timeAgo(ts) {
  if (!ts) return "";
  const d = ts.toDate?.() || ts;
  if (!d?.getTime) return "";
  const sec = (Date.now() - d.getTime()) / 1000;
  if (sec < 60) return "just now";
  if (sec < 3600) return Math.floor(sec / 60) + "m";
  if (sec < 86400) return Math.floor(sec / 3600) + "h";
  return d.toLocaleDateString();
}

function ymdFromTS(ts) {
  const d = ts?.toDate?.() || ts;
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayLabel(dateKey) {
  const [y, m, d] = dateKey.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, m - 1, d);
  const today = new Date();
  const ymd = (x) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(x.getDate()).padStart(2, "0")}`;
  const todayKey = ymd(today);
  const yest = new Date(today);
  yest.setDate(today.getDate() - 1);
  const yestKey = ymd(yest);
  if (dateKey === todayKey) return "Today";
  if (dateKey === yestKey) return "Yesterday";
  return dt.toLocaleDateString();
}

function roleFor(uid, active, myUid) {
  if (uid === myUid) return "You";
  if (uid === active?.hostUid) return "Host";
  if (uid === active?.guestUid) return "Guest";
  return "Participant";
}

function initialForRole(role) {
  return role?.[0]?.toUpperCase?.() || "?";
}

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

/** Redact sensitive-ish contact/payment content for MVP blur-only */
function redactText(t = "") {
  let s = String(t);

  // emails
  s = s.replace(
    /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi,
    "[email hidden]"
  );

  // urls
  s = s.replace(
    /\b(?:https?:\/\/|www\.)[^\s]+|\b[a-z0-9-]+\.(?:com|net|org|io|co|ai|app|me|tv|info|biz|ly|gg|link)\b/gi,
    "[link hidden]"
  );

  // phone-ish digit runs (broad on purpose)
  s = s.replace(/(\+?\d[\d\s\-().]{7,}\d)/g, "[number hidden]");

  return s;
}

/** Group messages by date, and mark boundaries for UI */
function useMessagePresentation(msgs, active, myUid) {
  const items = [];
  let prevBy = null;
  let prevAt = null;
  let prevDate = null;

  msgs.forEach((m, idx) => {
    const dateKey = ymdFromTS(m.at);
    if (dateKey && dateKey !== prevDate) {
      items.push({ kind: "day", id: `day-${dateKey}-${idx}`, dateKey });
      prevBy = null;
      prevAt = null;
      prevDate = dateKey;
    }

    const thisBy = m.by || "";
    const thisAt = m.at?.toDate?.() || m.at || null;
    const gapMin =
      prevAt && thisAt
        ? Math.abs(thisAt.getTime() - prevAt.getTime()) / 60000
        : 999;

    const newGroup = thisBy !== prevBy || gapMin > 6;
    items.push({
      kind: "msg",
      id: m.id,
      ...m,
      role: roleFor(thisBy, active, myUid),
      showHeader: newGroup,
      mine: thisBy === myUid,
    });

    prevBy = thisBy;
    prevAt = thisAt;
  });

  return items;
}

/** ------------------------ component ------------------------ **/
export default function Messages() {
  const auth = getAuth();
  const [me, setMe] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  // Ensure auth is ready before any subscription
  useEffect(() => {
    const off = onAuthStateChanged(auth, (u) => {
      setMe(u || null);
      setAuthReady(true);
    });
    return off;
  }, [auth]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [threads, setThreads] = useState([]);
  const [threadsError, setThreadsError] = useState("");
  const [active, setActive] = useState(null);

  const [msgs, setMsgs] = useState([]);
  const [msgsError, setMsgsError] = useState("");

  const [text, setText] = useState("");
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const [showJump, setShowJump] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const atBottomRef = useRef(true);

  // moderation UX state
  const [moderationError, setModerationError] = useState("");
  const [moderationWarn, setModerationWarn] = useState("");

  // Subscribe to my inbox (after auth is ready)
  useEffect(() => {
    setThreadsError("");
    if (!authReady || !me?.uid) return;
    const off = watchThreadsForUser({
      uid: me.uid,
      onChange: (rows) => {
        setThreads(rows);
        setThreadsError("");
      },
      onError: (e) => {
        console.error("threads listener:", e);
        setThreadsError("Could not load your inbox (permissions).");
      },
    });
    return off;
  }, [authReady, me?.uid]);

  // Pick active thread from ?thread=… or default first
  useEffect(() => {
    if (!threads?.length) return setActive(null);
    const tid = searchParams.get("thread");
    const found = threads.find((t) => t.id === tid) || threads[0];
    setActive(found || null);
  }, [threads, searchParams]);

  // Watch messages of active thread
  useEffect(() => {
    setMsgsError("");
    if (!active) return setMsgs([]);
    const off = watchMessages({
      threadId: active.id,
      onChange: (xs) => {
        setMsgs(xs);
        if (me?.uid) markThreadRead({ threadId: active.id, uid: me.uid });
        if (!atBottomRef.current) setShowJump(true);
      },
      onError: (e) => {
        console.error("messages listener:", e);
        setMsgsError("Could not load messages (permissions).");
      },
    });
    return off;
  }, [active?.id, me?.uid]);

  // Auto-scroll to bottom on new messages IF you were already at bottom
  useEffect(() => {
    if (!listRef.current) return;
    if (atBottomRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight + 999;
      setShowJump(false);
    }
  }, [msgs.length]);

  // Track scroll position for jump button
  function onScroll() {
    const el = listRef.current;
    if (!el) return;
    const nearBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight) < 80;
    atBottomRef.current = nearBottom;
    setAtBottom(nearBottom);
    if (nearBottom) setShowJump(false);
  }

  function jumpToBottom() {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight + 999, behavior: "smooth" });
    atBottomRef.current = true;
    setAtBottom(true);
    setShowJump(false);
  }

  // Auto-resize composer
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 180) + "px";
  }, [text]);

  async function onSend(e) {
    e?.preventDefault?.();

    setModerationError("");
    setModerationWarn("");

    const trimmed = text.trim();
    if (!trimmed || !active) return;

    const sending = trimmed.slice(0, MAX_LEN);

    // Only use prior-window context if THIS message seems contact-ish
    const needsContext =
      /(\d|zero|one|two|three|four|five|six|seven|eight|nine|ten|phone|number|call|text|reach|contact|email|dm|insta|instagram|snap|whatsapp|telegram|signal|venmo|cashapp|zelle|paypal|outside|off\s*app|off\s*platform|direct|discount|\(|\)|-|\+)/i.test(
        sending
      );

    const recentMsgs = msgs.slice(-8);
    const windowText = needsContext ? buildWindowText(recentMsgs) : "";

    const bookingStatus =
      active?.bookingStatus || active?.status || "none";

    const verdict = moderateMessage({
      text: sending,
      windowText,
      bookingStatus,
    });

    const redactedSending = redactText(sending);
    const willRedact =
      verdict.action === "block" || redactedSending !== sending;

    // ✅ MVP STORAGE RULE:
    // if flagged/redactable, store only redacted text so email notifications are safe
    const textToStore = willRedact ? redactedSending : sending;
    const safety = willRedact
      ? {
          action: verdict.action,
          hard: verdict.hard,
          soft: verdict.soft,
          preBooking: verdict.preBooking,
          redacted: true,
        }
      : null;

    if (verdict.action === "block") {
      setModerationError(
        "For safety, contact/payment info is hidden before booking."
      );
    } else if (verdict.action === "warn") {
      setModerationWarn(
        "Reminder: keep booking and payment on-platform so you stay protected."
      );
    }

    setText("");
    try {
      await sendMessage({
        threadId: active.id,
        text: textToStore,
        safety,
      });
    } catch (err) {
      setText(sending);
      console.error(err);

      if (err?.code === "failed-precondition") {
        setModerationError(
          "That message looks like it includes contact/payment info. Please remove it and try again."
        );
        return;
      }

      alert("Failed to send. Please try again.");
    }
  }

  function onComposerKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  }

  function onChangeText(e) {
    const v = e.target.value;
    if (v.length <= MAX_LEN) setText(v);
    else setText(v.slice(0, MAX_LEN));
  }

  function openThread(tid) {
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      next.set("thread", tid);
      return next;
    });
  }

  const myUid = me?.uid;
  const iAmHost = active && myUid && active.hostUid === myUid;

  const present = useMessagePresentation(msgs, active, myUid);

  const quickReplies = [
    "Is this available on my date?",
    "Could I tour the space?",
    "What’s the exact address?",
    "Any rules I should know?",
  ];

  return (
    <div className="mx-auto grid max-w-6xl grid-cols-1 gap-0 px-0 py-0 md:gap-6 md:px-4 md:py-8 md:grid-cols-3">
      {/* Inbox (desktop only) */}
      <aside className="md:col-span-1 md:block hidden">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-xl font-semibold">Messages</h1>
          <Link
            to="/explore"
            className="rounded-full border border-neutral-300 px-3 py-1 text-sm hover:bg-neutral-50"
          >
            Find spaces
          </Link>
        </div>

        <div className="overflow-hidden rounded-2xl border border-neutral-200">
          {!authReady ? (
            <div className="p-6 text-sm text-neutral-600">Loading…</div>
          ) : !me ? (
            <div className="p-6 text-sm text-neutral-600">
              Please sign in to view your inbox.
            </div>
          ) : threadsError ? (
            <div className="p-6 text-sm text-red-700">{threadsError}</div>
          ) : threads.length === 0 ? (
            <div className="p-6 text-sm text-neutral-600">
              No conversations yet.
            </div>
          ) : (
            <ul className="max-h-[70vh] divide-y divide-neutral-100 overflow-y-auto">
              {threads.map((t) => {
                const isActive = active?.id === t.id;
                const unread = (t.unread && t.unread[myUid]) || 0;
                return (
                  <li
                    key={t.id}
                    onClick={() => openThread(t.id)}
                    className={classNames(
                      "cursor-pointer px-4 py-3 transition",
                      isActive ? "bg-neutral-50" : "hover:bg-neutral-50"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 overflow-hidden rounded-lg border border-neutral-200">
                        <img
                          src={t.listingPhoto || "/favicon.ico"}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <div className="truncate font-medium">
                            {t.listingTitle || "Conversation"}
                          </div>
                          <div
                            className="ml-2 shrink-0 text-xs text-neutral-500"
                            title={
                              t.lastAt?.toDate?.()?.toLocaleString?.() || ""
                            }
                          >
                            {timeAgo(t.lastAt)}
                          </div>
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-sm text-neutral-600">
                          <span className="truncate">
                            {snippet(t.lastMsg?.text || "")}
                          </span>
                          {unread > 0 && (
                            <span
                              className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-black px-1.5 text-[11px] font-semibold text-white"
                              title={`${unread} unread`}
                            >
                              {unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>

      {/* Chat pane */}
      <main className="md:col-span-2">
        {!active ? (
          <div className="flex h-[100dvh] md:h-[70vh] items-center justify-center rounded-none md:rounded-2xl border-b md:border border-dashed border-neutral-300 bg-white">
            <div className="px-6 text-center">
              <div className="text-lg font-medium">Select a conversation</div>
              <p className="mt-1 text-sm text-neutral-600">
                Start from a listing page via “Message Host” or open a booking
                and tap “Contact”.
              </p>
              <Link
                to="/explore"
                className="mt-4 inline-flex rounded-full border border-neutral-300 px-4 py-2 text-sm font-medium hover:bg-neutral-50 md:hidden"
              >
                Browse spaces
              </Link>
            </div>
          </div>
        ) : (
          <div className="relative flex h-[100dvh] md:h-[70vh] flex-col overflow-hidden rounded-none md:rounded-2xl border-b md:border border-neutral-200 bg-white">
            {/* Mobile top bar */}
            <div className="sticky top-0 z-20 flex items-center gap-2 border-b border-neutral-200 bg-white px-3 py-2 md:hidden">
              <button
                type="button"
                onClick={() => setActive(null)}
                className="rounded-full px-2 py-1 text-sm hover:bg-neutral-100"
                title="Back"
              >
                ←
              </button>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs text-neutral-500">
                  {iAmHost ? "Guest" : "Host"}
                </div>
                <div className="truncate font-semibold">
                  {active.listingTitle || "Conversation"}
                </div>
              </div>
              {active.listingId ? (
                <Link
                  to={`/listing/${active.listingId}`}
                  className="rounded-full border border-neutral-200 px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-50"
                >
                  View
                </Link>
              ) : null}
            </div>

            {/* Header (desktop) */}
            <div className="sticky top-0 z-10 hidden md:flex items-center justify-between border-b border-neutral-200 bg-white/80 px-4 py-3 backdrop-blur">
              <div className="min-w-0">
                <div className="truncate text-xs text-neutral-500">
                  {iAmHost ? "Guest" : "Host"}
                </div>
                <div className="truncate font-semibold flex items-center gap-2">
                  {active.listingTitle || "—"}
                  {active.listingId ? (
                    <Link
                      to={`/listing/${active.listingId}`}
                      className="rounded-full border border-neutral-200 px-2 py-[2px] text-xs text-neutral-700 hover:bg-neutral-50"
                      title="View listing"
                    >
                      View
                    </Link>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {active.bookingId ? (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                    Booking • {active.bookingId.slice(0, 6)}
                  </span>
                ) : (
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                    Inquiry
                  </span>
                )}
                <button
                  type="button"
                  className="rounded-full border border-neutral-200 px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-50"
                  title="Copy thread ID"
                  onClick={() => {
                    navigator.clipboard?.writeText(active.id);
                  }}
                >
                  Copy ID
                </button>
              </div>
            </div>

            {/* Message list */}
            <div
              ref={listRef}
              className="flex-1 space-y-3 overflow-y-auto p-4 bg-white"
              onScroll={onScroll}
            >
              {msgsError ? (
                <div className="py-8 text-center text-sm text-red-700">
                  {msgsError}
                </div>
              ) : present.length === 0 ? (
                <div className="py-8 text-center text-sm text-neutral-500">
                  Say hi 👋
                </div>
              ) : (
                present.map((it) =>
                  it.kind === "day" ? (
                    <div
                      key={it.id}
                      className="sticky top-2 z-0 mb-2 flex justify-center"
                    >
                      <span className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs text-neutral-600 shadow-sm">
                        {dayLabel(it.dateKey)}
                      </span>
                    </div>
                  ) : (
                    <div
                      key={it.id}
                      className={classNames(
                        "flex",
                        it.mine ? "justify-end" : "justify-start"
                      )}
                    >
                      <div
                        className={classNames(
                          "flex max-w-[85%] md:max-w-[80%] items-end gap-2",
                          it.mine ? "flex-row-reverse" : "flex-row"
                        )}
                      >
                        {!it.mine && it.showHeader ? (
                          <div
                            className="mb-1 grid h-7 w-7 place-items-center rounded-full border border-neutral-200 bg-white text-xs font-bold text-neutral-600"
                            title={it.role}
                          >
                            {initialForRole(it.role)}
                          </div>
                        ) : (
                          <div className="h-7 w-7" />
                        )}

                        <div className="min-w-0">
                          {it.showHeader ? (
                            <div
                              className={classNames(
                                "mb-1 text-xs",
                                it.mine
                                  ? "text-neutral-500 text-right"
                                  : "text-neutral-600"
                              )}
                            >
                              {it.role}
                            </div>
                          ) : null}

                          <div
                            className={classNames(
                              "whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm leading-relaxed shadow-sm",
                              it.mine
                                ? "bg-black text-white"
                                : "border border-neutral-200 bg-neutral-100 text-neutral-900"
                            )}
                            title={
                              it.at?.toDate?.()?.toLocaleString?.() || ""
                            }
                          >
                            {it.imageUrl ? (
                              <a
                                href={it.imageUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="mb-1 block underline"
                              >
                                Image
                              </a>
                            ) : null}

                            {it.text ? (
                              (() => {
                                // Render-time moderation: per-message only (no window)
                                const bookingStatusNow =
                                  active?.bookingStatus ||
                                  active?.status ||
                                  "none";

                                const v = moderateMessage({
                                  text: it.text,
                                  windowText: "",
                                  bookingStatus: bookingStatusNow,
                                });

                                const redacted = redactText(it.text);
                                const wasRedacted = redacted !== it.text;

                                const shouldBlur = wasRedacted;
                                const shown = shouldBlur ? redacted : it.text;

                                return (
                                  <div
                                    className={
                                      shouldBlur
                                        ? "blur-[2px] select-none"
                                        : ""
                                    }
                                  >
                                    {shown}
                                  </div>
                                );
                              })()
                            ) : null}

                            {it.text &&
                              (() => {
                                const redacted = redactText(it.text);
                                if (redacted === it.text) return null;
                                return (
                                  <div className="mt-1 text-[11px] text-amber-700">
                                    Hidden for safety · keep contact/payment
                                    on-platform
                                  </div>
                                );
                              })()}
                          </div>

                          <div
                            className={classNames(
                              "mt-1 text-[11px]",
                              it.mine
                                ? "text-right text-neutral-500"
                                : "text-neutral-500"
                            )}
                            title={
                              it.at?.toDate?.()?.toLocaleString?.() || ""
                            }
                          >
                            {timeAgo(it.at)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )
              )}
            </div>

            {showJump && (
              <button
                type="button"
                onClick={jumpToBottom}
                className="absolute bottom-24 md:bottom-20 left-1/2 z-10 -translate-x-1/2 rounded-full border border-neutral-200 bg-white/90 px-3 py-1 text-xs text-neutral-700 shadow hover:bg-white"
                title="Jump to latest"
              >
                New messages ↓
              </button>
            )}

            {/* Composer */}
            <form
              onSubmit={onSend}
              className="sticky bottom-0 border-t border-neutral-200 bg-white/95 p-3 backdrop-blur md:bg-white/80"
            >
              <div className="mb-2 flex flex-wrap gap-2 overflow-x-auto md:overflow-visible">
                {quickReplies.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => {
                      setModerationError("");
                      setModerationWarn("");
                      setText((t) => (t ? t : q));
                    }}
                    className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs text-neutral-700 hover:bg-neutral-50 whitespace-nowrap"
                    title="Insert suggestion"
                  >
                    {q}
                  </button>
                ))}
              </div>

              {moderationError && (
                <div className="mb-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                  {moderationError}
                </div>
              )}
              {moderationWarn && !moderationError && (
                <div className="mb-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {moderationWarn}
                </div>
              )}

              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  rows={1}
                  placeholder="Write a message…"
                  value={text}
                  onChange={onChangeText}
                  onKeyDown={onComposerKeyDown}
                  className="min-h-[48px] max-h-[180px] flex-1 resize-none rounded-2xl border border-neutral-300 px-3 py-3 text-[15px] outline-none focus:border-neutral-500"
                />
                <button
                  type="submit"
                  disabled={!text.trim()}
                  className="rounded-2xl bg-black px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#111] disabled:opacity-50"
                  title="Press Enter to send • Shift+Enter for newline"
                >
                  Send
                </button>
              </div>

              <div className="mt-1 flex items-center justify-between text-[11px] text-neutral-500">
                <div>
                  Press <kbd className="rounded border px-1">Enter</kbd> to send ·{" "}
                  <kbd className="rounded border px-1">Shift</kbd>+
                  <kbd className="rounded border px-1">Enter</kbd> for a new line
                </div>
                <div>
                  {text.length}/{MAX_LEN}
                </div>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
